import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { Calendar, Flag, Square } from 'lucide-react';
import { type Task, type Milestone } from '../packages/api-client/pm-api';
import { type Phase } from '../packages/api-client/plan-api';
import { varAlpha } from '../utils/statusColors';

// ── Layout constants ────────────────────────────────────────────────────────
// Fit-to-range: the day width is computed at runtime so the whole project span
// fits the visible area. These bound it — never thinner than MIN (bars stay
// visible; very long plans scroll a little) nor wider than MAX (short plans
// don't balloon). LABEL_COL_PX matches .gantt-label-col / .gantt-row-label.
const MIN_DAY_WIDTH = 3;
const MAX_DAY_WIDTH = 40;
const LABEL_COL_PX = 220;

// ── Date helpers ────────────────────────────────────────────────────────────
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function diffDays(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

import { buildGanttItems } from './ganttRows';
export { buildGanttItems } from './ganttRows';
export type { GanttRow, GanttItem } from './ganttRows';

// ── Props ────────────────────────────────────────────────────────────────────
export interface GanttChartProps {
  tasks: Task[];
  milestones: Milestone[];
  phases?: Phase[];
  criticalTaskIds?: Set<string>;
  onBarClick?: (taskId: string) => void;
  /** Optional: map of taskId → baseline {start?, due?} dates (ISO strings) */
  baselineByTask?: Map<string, { start?: string; due?: string }>;
  /** Optional: map of taskId → slip in days (positive=late, negative=ahead, 0=on track) */
  slipByTask?: Map<string, number>;
  /** Optional: map of milestoneId → baseline target date (ISO string) */
  baselineMilestones?: Map<string, string>;
  /** Optional: map of milestoneId → health/projection data */
  milestoneHealth?: Map<string, { health: string; projected?: string | null; target?: string | null; floatDays?: number | null }>;
  /** Optional: map of phaseId → rollup { start?, end?, taskCount, doneCount } */
  phaseRollups?: Map<string, { start?: string | null; end?: string | null; taskCount: number; doneCount: number }>;
}

// ── Component ────────────────────────────────────────────────────────────────
// ── Milestone health color map ───────────────────────────────────────────────
const HEALTH_COLOR: Record<string, string> = {
  on_track: 'var(--warn)',
  late: 'var(--danger)',
  done: 'var(--ok)',
  no_target: 'var(--warn)',
};

// ── Text color for health-overridden fills ──────────────────────────────────
const HEALTH_TEXT_COLOR: Record<string, string> = {
  on_track: 'var(--on-warn)',
  late: 'var(--on-danger)',
  done: 'var(--on-ok)',
  no_target: 'var(--on-warn)',
};

export function GanttChart({ tasks, milestones, phases, criticalTaskIds, onBarClick, baselineByTask, slipByTask, baselineMilestones, milestoneHealth, phaseRollups }: GanttChartProps) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const displayGroups = useMemo(
    () => buildGanttItems(tasks, milestones, phases, criticalTaskIds),
    [tasks, milestones, phases, criticalTaskIds],
  );
  const allRows = useMemo(
    () => displayGroups.flatMap(i => (i.type === 'row' ? [i.row] : [])),
    [displayGroups],
  );

  const { chartStart, totalDays } = useMemo(() => {
    const dates = allRows.flatMap(r => [r.startDate, r.dueDate]).filter(Boolean) as Date[];
    if (dates.length === 0) return { chartStart: addDays(today, -7), totalDays: 60 };
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
    const start = addDays(min, -3);
    const days = Math.max(60, diffDays(start, addDays(max, 10)));
    return { chartStart: start, totalDays: days };
  }, [allRows, today]);

  // Measure the visible timeline width (container minus the fixed label column)
  // and derive the per-day pixel width so the full span fits without scrolling.
  const containerRef = useRef<HTMLDivElement>(null);
  const [availW, setAvailW] = useState(0);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setAvailW(el.clientWidth - LABEL_COL_PX);
    measure();
    if (typeof ResizeObserver === 'undefined') return; // jsdom / SSR
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dayPx = useMemo(() => {
    if (availW <= 0) return 32; // pre-measure fallback (and the jsdom test path)
    return Math.max(MIN_DAY_WIDTH, Math.min(MAX_DAY_WIDTH, availW / totalDays));
  }, [availW, totalDays]);

  // Header ticks + gridlines, bucketed by zoom so labels never collide:
  // every day when wide, weekly (Mondays) when medium, monthly when zoomed out.
  const ticks = useMemo(() => {
    const interval: 'day' | 'week' | 'month' = dayPx >= 22 ? 'day' : dayPx >= 9 ? 'week' : 'month';
    // A 12px month label ("Jul") needs ~26px; below that it spills into the 2nd, whose label is dropped.
    const narrowDays = interval === 'day' && dayPx < 26;
    const out: { i: number; label: string; isToday: boolean }[] = [];
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(chartStart, i);
      const show = interval === 'day'
        || (interval === 'week' && d.getDay() === 1)
        || (interval === 'month' && d.getDate() === 1);
      if (!show) continue;
      const label = interval === 'month'
        ? d.toLocaleDateString('en', { month: 'short', year: '2-digit' })
        : d.getDate() === 1
          // Daily ticks are one day wide, so the 1st shows just the month ("Oct") to fit at 12px.
          ? d.toLocaleDateString('en', interval === 'day' ? { month: 'short' } : { month: 'short', day: 'numeric' })
          : narrowDays && d.getDate() === 2 ? '' : d.getDate().toString();
      out.push({ i, label, isToday: d.toDateString() === today.toDateString() });
    }
    return out;
  }, [chartStart, totalDays, dayPx, today]);

  const trackWidth = totalDays * dayPx;
  const todayLeft = diffDays(chartStart, today) * dayPx;

  // ── Empty state ──────────────────────────────────────────────────────────
  if (allRows.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
          <Calendar size={48} aria-hidden="true" />
        </div>
        <div style={{ fontWeight: 600 }}>No timeline data</div>
        <div style={{ marginTop: '0.5rem', fontSize: 'var(--font-size-sm)' }}>
          Add start or due dates to tasks to see them here
        </div>
      </div>
    );
  }

  // ── Chart ────────────────────────────────────────────────────────────────
  return (
    <div className="gantt-container" ref={containerRef}>
      {/* Header */}
      <div className="gantt-header-row">
        <div className="gantt-label-col">Task / Milestone</div>
        <div className="gantt-timeline-header" style={{ position: 'relative', display: 'block', width: trackWidth, minHeight: '1.7rem' }}>
          {ticks.map(t => (
            <div
              key={t.i}
              className={`gantt-day-header${t.isToday ? ' today' : ''}`}
              style={{ position: 'absolute', left: t.i * dayPx, width: 'auto', minWidth: 0, borderRight: 'none', textAlign: 'left', paddingLeft: 2, whiteSpace: 'nowrap' }}
            >
              {t.label}
            </div>
          ))}
        </div>
      </div>

      {/* Rows */}
      {displayGroups.map((item, idx) => {
        if (item.type === 'header') {
          const rollup = item.phaseId ? phaseRollups?.get(item.phaseId) : undefined;
          const spanLeft = rollup?.start ? diffDays(chartStart, new Date(rollup.start)) * dayPx : null;
          const spanRight = rollup?.end ? diffDays(chartStart, new Date(rollup.end)) * dayPx + dayPx : null;
          const spanWidth = spanLeft !== null && spanRight !== null ? spanRight - spanLeft : null;
          const progressLabel = rollup
            ? `${rollup.doneCount}/${rollup.taskCount} · ${rollup.taskCount ? Math.round(rollup.doneCount / rollup.taskCount * 100) : 0}%`
            : null;
          return (
            <div key={`header-${idx}`} className="gantt-row" style={{ background: 'var(--color-surface-2)' }}>
              <div
                className="gantt-row-label"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontWeight: 600,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                  borderLeft: item.color ? `3px solid ${item.color}` : undefined,
                  paddingLeft: item.color ? '0.5rem' : undefined,
                }}
              >
                {item.label}
              </div>
              <div className="gantt-bar-area" style={{ position: 'relative', flex: 1, minWidth: totalDays * dayPx }}>
                <div className="gantt-today-line" style={{ left: todayLeft }} />
                {rollup && spanLeft !== null && spanWidth !== null && spanWidth > 0 && item.phaseId && (
                  <>
                    <div
                      data-testid={`gantt-phase-${item.phaseId}`}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: spanLeft,
                        width: spanWidth,
                        height: '6px',
                        borderRadius: 3,
                        background: item.color ?? 'var(--border)',
                        opacity: 0.3,
                        pointerEvents: 'none',
                        zIndex: 0,
                      }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: spanLeft + spanWidth + 6,
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 500,
                        color: 'var(--ink-muted)',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    >
                      {progressLabel}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        }

        const row = item.row;
        const barStart = row.startDate ? diffDays(chartStart, row.startDate) * dayPx : null;
        const barWidth =
          row.startDate && row.dueDate
            ? Math.max((diffDays(row.startDate, row.dueDate) + 1) * dayPx, dayPx)
            : row.startDate ? dayPx : null;

        return (
          <div key={row.id} className="gantt-row">
            <div
              className={`gantt-row-label${row.isMilestone ? ' milestone' : ''}`}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              {row.isMilestone ? <Flag size={14} aria-hidden="true" /> : <Square size={14} aria-hidden="true" />}
              {row.label}
            </div>
            <div className="gantt-bar-area" style={{ position: 'relative', flex: 1, minWidth: totalDays * dayPx }}>
              {/* Today line */}
              <div className="gantt-today-line" style={{ left: todayLeft }} />

              {/* Gridlines at tick positions */}
              {ticks.map(t => (
                <div
                  key={t.i}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: t.i * dayPx,
                    width: 1,
                    background: varAlpha('ink', 0.04),
                    pointerEvents: 'none',
                  }}
                />
              ))}

              {/* Ghost baseline bar (rendered first / behind live bar) */}
              {!row.isMilestone && (() => {
                const baseline = baselineByTask?.get(row.id);
                if (!baseline) return null;
                const bStart = baseline.start
                  ? new Date(baseline.start)
                  : baseline.due ? addDays(new Date(baseline.due), -1) : null;
                const bDue = baseline.due ? new Date(baseline.due) : null;
                if (!bStart) return null;
                const ghostLeft = diffDays(chartStart, bStart) * dayPx;
                const ghostWidth = bDue
                  ? Math.max((diffDays(bStart, bDue) + 1) * dayPx, dayPx)
                  : dayPx;
                return (
                  <div
                    data-testid={`gantt-baseline-${row.id}`}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      left: ghostLeft,
                      width: ghostWidth,
                      height: '8px',
                      borderRadius: 4,
                      background: 'var(--border)',
                      opacity: 0.5,
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  />
                );
              })()}

              {/* Ghost baseline diamond for milestones */}
              {row.isMilestone && (() => {
                const baselineDateStr = baselineMilestones?.get(row.id);
                if (!baselineDateStr) return null;
                const bDate = new Date(baselineDateStr);
                const ghostLeft = diffDays(chartStart, bDate) * dayPx;
                return (
                  <div
                    data-testid={`gantt-baseline-milestone-${row.id}`}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%) rotate(45deg)',
                      left: ghostLeft + dayPx / 2 - 6,
                      width: 12,
                      height: 12,
                      background: 'var(--border)',
                      opacity: 0.5,
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  />
                );
              })()}

              {barStart !== null && barWidth !== null && (() => {
                // For milestones with health data, override color and possibly position
                const h = row.isMilestone ? milestoneHealth?.get(row.id) : undefined;
                const effectiveColor = h ? (HEALTH_COLOR[h.health] ?? row.color) : row.color;
                // When projected date is set, reposition the diamond there
                const effectiveLeft = (h?.projected && row.isMilestone)
                  ? diffDays(chartStart, new Date(h.projected)) * dayPx
                  : barStart;

                const barStyle: React.CSSProperties = {
                  left: effectiveLeft,
                  width: barWidth,
                  background: effectiveColor,
                  // When health overrides the fill, set matching text color; otherwise
                  // .gantt-task-bar / .gantt-milestone-bar CSS classes handle it.
                  ...(h ? { color: HEALTH_TEXT_COLOR[h.health] ?? 'var(--on-accent)' } : {}),
                  position: 'absolute',
                  zIndex: 1,
                  ...(row.critical
                    ? { boxShadow: `0 0 0 2px var(--warn), 0 0 8px ${varAlpha('accent', 0.55)}` }
                    : {}),
                };

                // Build tooltip for health milestones
                const msTitle = h
                  ? `Projected ${h.projected ?? '—'} · Target ${h.target ?? '—'} · ${h.floatDays != null ? (h.floatDays >= 0 ? '+' : '') + h.floatDays + 'd' : '—'}`
                  : row.label;

                return (
                  <>
                    {/* Faint reference tick at target date when projected is set */}
                    {row.isMilestone && h?.projected && h?.target && (() => {
                      const targetLeft = diffDays(chartStart, new Date(h.target)) * dayPx;
                      return (
                        <div
                          style={{
                            position: 'absolute',
                            top: '10%',
                            bottom: '10%',
                            left: targetLeft + dayPx / 2,
                            width: 1,
                            background: 'var(--ink-muted)',
                            pointerEvents: 'none',
                            zIndex: 0,
                          }}
                        />
                      );
                    })()}
                    <div
                      className={`gantt-bar ${row.isMilestone ? 'gantt-milestone-bar' : 'gantt-task-bar'}`}
                      style={barStyle}
                      title={msTitle}
                      onClick={() => {
                        if (!row.isMilestone) {
                          onBarClick?.(row.id);
                        }
                      }}
                      data-label={barWidth > 60 ? row.label : undefined}
                      {...(h ? { 'data-testid': `gantt-ms-${row.id}`, 'data-health': h.health } : {})}
                    >
                      {barWidth > 60 && row.label}
                    </div>
                  </>
                );
              })()}

              {/* Slip badge */}
              {!row.isMilestone && (() => {
                const slip = slipByTask?.get(row.id);
                if (slip === undefined) return null;
                const badgeText = slip > 0 ? `+${slip}d` : slip < 0 ? `−${Math.abs(slip)}d` : 'on track';
                const badgeColor = slip > 0
                  ? 'var(--danger)'
                  : slip < 0
                    ? 'var(--ok)'
                    : 'var(--ink-muted)';
                const anchorLeft = barStart !== null ? barStart + (barWidth ?? 0) + 4 : 4;
                return (
                  <span
                    data-testid={`gantt-slip-${row.id}`}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      left: anchorLeft,
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      color: badgeColor,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      zIndex: 2,
                    }}
                  >
                    {badgeText}
                  </span>
                );
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
}
